import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { LogIn, Menu, X } from 'lucide-react'
import { buttonClass } from '@/lib/button'
import { Logo } from '@/components/generals/logo'

import { navLinks } from '@/constants/content'

export const SiteHeader: FC = (): ReactNode => {
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        if (!menuOpen) return

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [menuOpen])

    const closeMenu = (): void => setMenuOpen(false)

    return (
        <header className='sticky top-0 z-50 border-b border-white/10 bg-ink/90 text-white backdrop-blur-xl'>
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:gap-6 sm:px-6 lg:h-[72px] lg:px-8'>
                <Link
                    href='/'
                    aria-label="Webify IT Technologies home"
                    className='rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal'
                >
                    <Logo tone='light' />
                </Link>

                <nav aria-label="Main" className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='rounded-lg px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href='/#register'
                        className='hidden h-10 items-center rounded-lg px-4 text-sm font-semibold text-white/90 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal sm:inline-flex'
                    >
                        Register
                    </a>

                    <a href='/#sign-in' className={buttonClass('light', 'sm')}>
                        <LogIn className='hidden size-4 sm:block' aria-hidden='true' />
                        Sign in
                    </a>

                    <button
                        type='button'
                        onClick={() => setMenuOpen((open) => !open)}
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        className='flex size-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal lg:hidden'
                    >
                        {menuOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div id='mobile-menu' className='border-t border-white/10 lg:hidden motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2'>
                    <nav aria-label="Mobile">
                        <ul className='mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4 sm:px-6'>
                            {navLinks.map((link) => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        onClick={closeMenu}
                                        className='flex h-11 items-center rounded-lg px-3 text-base font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white'
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}

                            <li className='pt-2'>
                                <a href='/#register' onClick={closeMenu} className={buttonClass('accent', 'lg', 'w-full')}>
                                    Register for a program
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}
        </header>
    )
}
